import apiClient from './client';
import { Project, Pagination, ApiResponse } from '@/types';

/**
 * Project query parameters
 */
export interface ProjectQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Project list response
 */
export interface ProjectListResponse {
  data: Project[];
  pagination: Pagination;
}

/**
 * Get projects with filters
 */
export async function getProjects(params?: ProjectQueryParams): Promise<ProjectListResponse> {
  const response = await apiClient.get<ApiResponse<Project[]> & { meta?: { pagination: Pagination } }>('/projects', { params });
  return {
    data: response.data.data as Project[],
    pagination: response.data.meta?.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
  };
}

/**
 * Get a single project by ID
 */
export async function getProject(id: string): Promise<Project> {
  const response = await apiClient.get<ApiResponse<Project>>(`/projects/${id}`);
  return response.data.data as Project;
}

/**
 * Create project data
 */
export interface CreateProjectData {
  name: string;
  code: string;
  description?: string;
  managerId?: string;
  settings?: {
    standupTime?: string;
    standupTimezone?: string;
    inactivityThresholdDays?: number;
    requireIssueLinks?: boolean;
    staleIssueDays?: number;
    autoCommentOnStale?: boolean;
    notifyOnStale?: boolean;
  };
}

/**
 * Create a new project
 */
export async function createProject(data: CreateProjectData): Promise<Project> {
  const response = await apiClient.post<ApiResponse<Project>>('/projects', data);
  return response.data.data as Project;
}

/**
 * Update project data
 */
export interface UpdateProjectData {
  name?: string;
  description?: string;
  managerId?: string;
  settings?: {
    standupTime?: string;
    standupTimezone?: string;
    inactivityThresholdDays?: number;
    requireIssueLinks?: boolean;
    staleIssueDays?: number;
    autoCommentOnStale?: boolean;
    notifyOnStale?: boolean;
  };
  active?: boolean;
}

/**
 * Update a project
 */
export async function updateProject(id: string, data: UpdateProjectData): Promise<Project> {
  const response = await apiClient.patch<ApiResponse<Project>>(`/projects/${id}`, data);
  return response.data.data as Project;
}

/**
 * Delete (deactivate) a project
 */
export async function deleteProject(id: string): Promise<void> {
  await apiClient.delete(`/projects/${id}`);
}

/**
 * Add member to project
 */
export async function addProjectMember(projectId: string, userId: string): Promise<Project> {
  const response = await apiClient.post<ApiResponse<Project>>(
    `/projects/${projectId}/members`,
    { userId }
  );
  return response.data.data as Project;
}

/**
 * Remove member from project
 */
export async function removeProjectMember(projectId: string, userId: string): Promise<Project> {
  const response = await apiClient.delete<ApiResponse<Project>>(
    `/projects/${projectId}/members/${userId}`
  );
  return response.data.data as Project;
}

/**
 * Link repository to project
 */
export async function linkRepository(projectId: string, repoId: string): Promise<Project> {
  const response = await apiClient.post<ApiResponse<Project>>(
    `/projects/${projectId}/repos`,
    { repoId }
  );
  return response.data.data as Project;
}

/**
 * Unlink repository from project
 */
export async function unlinkRepository(projectId: string, repoId: string): Promise<Project> {
  const response = await apiClient.delete<ApiResponse<Project>>(
    `/projects/${projectId}/repos/${repoId}`
  );
  return response.data.data as Project;
}
